/**
 * Archivo de rutina compartida (Req. 27; design.md, 10.4).
 *
 * Una rutina se exporta a un archivo de texto para mandarlo por AirDrop,
 * WhatsApp o Archivos, y al importarlo se muestra antes de guardar nada
 * (Req. 27.2). Importar solo agrega esa rutina y los ejercicios que falten:
 * no toca ningún otro dato (Req. 27.3).
 *
 * Qué viaja y qué no:
 * - Viaja la rutina completa: su nombre, sus notas, sus días, su modalidad y
 *   cada ejercicio con su objetivo, descanso, indicaciones, biserie, pausa,
 *   variante y si se anota por lado. Si no viajara, el que la recibe perdería
 *   en silencio la mitad de cómo se hace.
 * - No viaja nada de cada teléfono: la unidad con que se captura la carga, el
 *   incremento del ejercicio ni sus notas personales ("asiento en 4"). Esos
 *   son de quien entrena, no de la rutina, y el que la recibe usa los suyos.
 * - No viajan cargas, series hechas ni historial: una rutina es el plan.
 *
 * Módulo puro: sin React y sin Dexie.
 */

import type { Equipo, Exercise, GrupoMuscular, Modalidad, Routine, RoutineExercise, TipoEjercicio } from '@/db/types';
import { normalizar } from './busqueda';

export const APP_RUTINA = 'app-gym-salud';
export const TIPO_RUTINA = 'rutina';
/** Sube solo si el contenido necesita migración al leerlo. */
export const FORMATO_RUTINA = 1;

export const EJERCICIOS_MAXIMOS = 40;
/** Los mismos topes que el editor de rutinas, para que lo propio se pueda reimportar. */
const NOMBRE_MAXIMO = 60;
const NOTAS_DE_RUTINA = 600;
const NOTAS_MAXIMAS = 300;

const GRUPOS: readonly GrupoMuscular[] = [
  'pecho',
  'espalda',
  'hombros',
  'biceps',
  'triceps',
  'antebrazos',
  'abdomen',
  'gluteos',
  'cuadriceps',
  'femorales',
  'pantorrillas',
  'aductores',
  'cuerpo_completo',
  'cardio',
];
const EQUIPOS: readonly Equipo[] = [
  'barra',
  'mancuernas',
  'maquina',
  'polea',
  'peso_corporal',
  'kettlebell',
  'banda',
  'smith',
  'otro',
];
const TIPOS: readonly TipoEjercicio[] = ['peso_reps', 'reps', 'tiempo', 'distancia'];

export interface EjercicioDeArchivo {
  /** El id con que está en el teléfono que exportó: sirve para los del catálogo. */
  exerciseId: string;
  esBase: boolean;
  nombre: string;
  grupoMuscular: GrupoMuscular;
  equipo: Equipo;
  tipo: TipoEjercicio;
  series: number;
  repsMin: number | null;
  repsMax: number | null;
  descansoSeg: number;
  /** Las indicaciones de la rutina, no las notas personales del ejercicio. */
  notas: string | null;
  biserie?: string | null;
  descansoBiserieSeg?: number | null;
  pausaSeg?: number | null;
  variante?: string | null;
  porLado?: boolean;
}

export interface ArchivoRutina {
  app: string;
  tipo: string;
  formatVersion: number;
  exportedAt: string;
  rutina: {
    nombre: string;
    notas: string | null;
    dias?: number[];
    modalidad?: Modalidad;
    ejercicios: EjercicioDeArchivo[];
  };
}

type ItemDeRutina = Pick<
  RoutineExercise,
  'exerciseId' | 'series' | 'repsMin' | 'repsMax' | 'descansoSeg' | 'notas' | 'biserie' | 'descansoBiserieSeg' | 'pausaSeg' | 'variante' | 'porLado'
>;
type EjercicioDelCatalogo = Pick<Exercise, 'id' | 'nombre' | 'grupoMuscular' | 'equipo' | 'tipo' | 'esBase'>;

/** El archivo de una rutina, en el orden en que están sus ejercicios. */
export function armarArchivoDeRutina(
  rutina: Pick<Routine, 'nombre' | 'notas' | 'dias' | 'modalidad'>,
  ejercicios: readonly { item: ItemDeRutina; ejercicio: EjercicioDelCatalogo | undefined }[],
  exportedAt: string,
): ArchivoRutina {
  return {
    app: APP_RUTINA,
    tipo: TIPO_RUTINA,
    formatVersion: FORMATO_RUTINA,
    exportedAt,
    rutina: {
      nombre: rutina.nombre,
      notas: rutina.notas,
      ...(rutina.dias === undefined || rutina.dias.length === 0 ? {} : { dias: [...rutina.dias] }),
      ...(rutina.modalidad === undefined ? {} : { modalidad: rutina.modalidad }),
      ejercicios: ejercicios
        .filter((e) => e.ejercicio !== undefined)
        .map(({ item, ejercicio }) => {
          const e = ejercicio as EjercicioDelCatalogo;
          return {
            exerciseId: e.id,
            esBase: e.esBase,
            nombre: e.nombre,
            grupoMuscular: e.grupoMuscular,
            equipo: e.equipo,
            tipo: e.tipo,
            series: item.series,
            repsMin: item.repsMin,
            repsMax: item.repsMax,
            descansoSeg: item.descansoSeg,
            notas: item.notas,
            ...(item.biserie == null ? {} : { biserie: item.biserie }),
            ...(item.descansoBiserieSeg == null ? {} : { descansoBiserieSeg: item.descansoBiserieSeg }),
            ...(item.pausaSeg == null ? {} : { pausaSeg: item.pausaSeg }),
            ...(item.variante == null || item.variante === '' ? {} : { variante: item.variante }),
            ...(item.porLado === true ? { porLado: true } : {}),
          };
        }),
    },
  };
}

export function serializarArchivoDeRutina(archivo: ArchivoRutina): string {
  return `${JSON.stringify(archivo, null, 2)}\n`;
}

/** "rutina-lunes-gluteos-y-femorales.rutina.json" */
export function nombreDeArchivoDeRutina(nombre: string): string {
  const limpio = normalizar(nombre)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `rutina-${limpio === '' ? 'sin-nombre' : limpio}.rutina.json`;
}

// ---------------------------------------------------------------------------
// Leer un archivo (Req. 27.2)
// ---------------------------------------------------------------------------

export type ResultadoDeArchivo = { ok: true; archivo: ArchivoRutina } | { ok: false; error: string };

const esTexto = (v: unknown, max: number) => typeof v === 'string' && v.trim() !== '' && v.length <= max;
const textoONulo = (v: unknown, max: number) => v === null || v === undefined || (typeof v === 'string' && v.length <= max);
const entero = (v: unknown, min: number, max: number) => Number.isInteger(v) && (v as number) >= min && (v as number) <= max;
const enteroONulo = (v: unknown, min: number, max: number) => v === null || v === undefined || entero(v, min, max);

function revisarEjercicio(v: unknown, i: number): EjercicioDeArchivo | string {
  /** "El ejercicio 3", para empezar la frase. */
  const donde = `El ejercicio ${i + 1}`;
  /** "del ejercicio 3", para el resto de la frase. */
  const del = `del ejercicio ${i + 1}`;
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return `${donde} no se entiende.`;
  const e = v as Record<string, unknown>;
  if (typeof e['exerciseId'] !== 'string' || e['exerciseId'] === '') return `Falta el identificador ${del}.`;
  if (!esTexto(e['nombre'], NOMBRE_MAXIMO)) return `Falta el nombre ${del}.`;
  if (!GRUPOS.includes(e['grupoMuscular'] as GrupoMuscular)) return `${donde} trae un grupo muscular que no existe.`;
  if (!EQUIPOS.includes(e['equipo'] as Equipo)) return `${donde} trae un equipo que no existe.`;
  if (!TIPOS.includes(e['tipo'] as TipoEjercicio)) return `${donde} trae un tipo que no existe.`;
  if (!entero(e['series'], 1, 20)) return `${donde} trae un número de series que no se puede usar.`;
  if (!enteroONulo(e['repsMin'], 1, 200) || !enteroONulo(e['repsMax'], 1, 200)) {
    return `${donde} trae repeticiones que no se pueden usar.`;
  }
  if (!entero(e['descansoSeg'], 0, 3600)) return `${donde} trae un descanso que no se puede usar.`;
  if (!textoONulo(e['notas'], NOTAS_MAXIMAS)) return `${donde} trae indicaciones demasiado largas.`;
  if (!textoONulo(e['biserie'], 4)) return `${donde} trae una biserie que no se entiende.`;
  if (!enteroONulo(e['descansoBiserieSeg'], 0, 3600)) return `${donde} trae un descanso de biserie que no se puede usar.`;
  if (!enteroONulo(e['pausaSeg'], 0, 600)) return `${donde} trae una pausa que no se puede usar.`;
  if (!textoONulo(e['variante'], NOMBRE_MAXIMO)) return `${donde} trae una variante demasiado larga.`;
  if (e['porLado'] !== undefined && typeof e['porLado'] !== 'boolean') return `${donde} trae "por lado" dañado.`;
  const texto = (campo: string) => (typeof e[campo] === 'string' && e[campo] !== '' ? (e[campo] as string) : null);
  const numero = (campo: string) => (typeof e[campo] === 'number' ? (e[campo] as number) : null);
  return {
    exerciseId: e['exerciseId'],
    esBase: e['esBase'] === true,
    nombre: (e['nombre'] as string).trim(),
    grupoMuscular: e['grupoMuscular'] as GrupoMuscular,
    equipo: e['equipo'] as Equipo,
    tipo: e['tipo'] as TipoEjercicio,
    series: e['series'] as number,
    repsMin: numero('repsMin'),
    repsMax: numero('repsMax'),
    descansoSeg: e['descansoSeg'] as number,
    notas: texto('notas'),
    ...(texto('biserie') === null ? {} : { biserie: texto('biserie') }),
    ...(numero('descansoBiserieSeg') === null ? {} : { descansoBiserieSeg: numero('descansoBiserieSeg') }),
    ...(numero('pausaSeg') === null ? {} : { pausaSeg: numero('pausaSeg') }),
    ...(texto('variante') === null ? {} : { variante: texto('variante') }),
    ...(e['porLado'] === true ? { porLado: true } : {}),
  };
}

/**
 * Lee el texto de un archivo de rutina. Es estricto: si algo no se entiende,
 * no se importa a medias. Una rutina con huecos no es la rutina.
 */
export function analizarArchivoDeRutina(texto: string): ResultadoDeArchivo {
  let crudo: unknown;
  try {
    crudo = JSON.parse(texto);
  } catch {
    return { ok: false, error: 'Ese archivo no se puede leer: no tiene el formato de una rutina de DuoFit.' };
  }
  if (typeof crudo !== 'object' || crudo === null || Array.isArray(crudo)) {
    return { ok: false, error: 'Ese archivo no tiene el formato de una rutina de DuoFit.' };
  }
  const a = crudo as Record<string, unknown>;
  if (a['app'] !== APP_RUTINA) return { ok: false, error: 'Ese archivo no es de DuoFit.' };
  if (a['tipo'] !== TIPO_RUTINA) {
    return {
      ok: false,
      error:
        a['tipo'] === 'respaldo' || a['data'] !== undefined
          ? 'Ese es un respaldo completo, no una rutina. Los respaldos se restauran en Más → Respaldos.'
          : 'Ese archivo de DuoFit no es una rutina.',
    };
  }
  if (!Number.isInteger(a['formatVersion']) || (a['formatVersion'] as number) < 1) {
    return { ok: false, error: 'Ese archivo de rutina no dice su versión.' };
  }
  if ((a['formatVersion'] as number) > FORMATO_RUTINA) {
    return { ok: false, error: 'Ese archivo viene de una versión más nueva de la app: actualízala para abrirlo.' };
  }
  const r = a['rutina'];
  if (typeof r !== 'object' || r === null || Array.isArray(r)) return { ok: false, error: 'El archivo no trae la rutina.' };
  const rutina = r as Record<string, unknown>;
  if (!esTexto(rutina['nombre'], NOMBRE_MAXIMO)) return { ok: false, error: 'La rutina del archivo no tiene nombre.' };
  if (!textoONulo(rutina['notas'], NOTAS_DE_RUTINA)) {
    return { ok: false, error: 'Las notas de la rutina son demasiado largas.' };
  }
  const lista = rutina['ejercicios'];
  if (!Array.isArray(lista) || lista.length === 0) return { ok: false, error: 'Esa rutina no trae ejercicios.' };
  if (lista.length > EJERCICIOS_MAXIMOS) {
    return { ok: false, error: `Esa rutina trae más de ${EJERCICIOS_MAXIMOS} ejercicios: no parece una rutina.` };
  }
  const ejercicios: EjercicioDeArchivo[] = [];
  for (const [i, x] of lista.entries()) {
    const revisado = revisarEjercicio(x, i);
    if (typeof revisado === 'string') return { ok: false, error: revisado };
    ejercicios.push(revisado);
  }
  const dias = rutina['dias'];
  const diasBuenos = Array.isArray(dias) ? dias.filter((d) => entero(d, 0, 6)) : [];
  const modalidad = rutina['modalidad'];
  return {
    ok: true,
    archivo: {
      app: APP_RUTINA,
      tipo: TIPO_RUTINA,
      formatVersion: a['formatVersion'] as number,
      exportedAt: typeof a['exportedAt'] === 'string' ? a['exportedAt'] : '',
      rutina: {
        nombre: (rutina['nombre'] as string).trim().replace(/\s+/g, ' '),
        notas: typeof rutina['notas'] === 'string' && rutina['notas'] !== '' ? rutina['notas'] : null,
        ...(diasBuenos.length === 0 ? {} : { dias: [...new Set(diasBuenos)].sort() }),
        ...(modalidad === 'casa' || modalidad === 'gimnasio' ? { modalidad } : {}),
        ejercicios,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Vincular con los ejercicios del teléfono (Req. 27.2)
// ---------------------------------------------------------------------------

/** Cómo queda cada ejercicio del archivo en este teléfono. */
export type ComoSeVincula = 'base' | 'propio' | 'nuevo';

export interface EjercicioVinculado {
  del: EjercicioDeArchivo;
  como: ComoSeVincula;
  /** El ejercicio de este teléfono con el que se vinculó; null si se va a crear. */
  exerciseId: string | null;
  /** El nombre con que va a quedar aquí. */
  nombre: string;
  /** Algo que conviene decir de este ejercicio antes de guardar. */
  nota: string | null;
}

export interface PlanDeImportacion {
  nombre: string;
  ejercicios: EjercicioVinculado[];
  /** Cuántos se van a crear. */
  nuevos: number;
  advertencias: string[];
}

const conMayuscula = (t: string) => `${t.charAt(0).toLocaleUpperCase('es-MX')}${t.slice(1)}`;

export const ETIQUETA_VINCULO: Readonly<Record<ComoSeVincula, string>> = {
  base: 'Ya lo tienes (del catálogo)',
  propio: 'Ya lo tienes (tuyo)',
  nuevo: 'Se agrega a tus ejercicios',
};

/**
 * Qué va a pasar con cada ejercicio del archivo (Req. 27.2): los del catálogo
 * se vinculan por su identificador y los propios por su nombre, sin acentos
 * ni mayúsculas; los que falten se crean. Esto no escribe nada: es lo que se
 * muestra antes de guardar.
 */
export function planDeImportacion(
  archivo: ArchivoRutina,
  delTelefono: readonly (EjercicioDelCatalogo & { archivado?: boolean })[],
  nombre: string = archivo.rutina.nombre,
): PlanDeImportacion {
  const porId = new Map(delTelefono.map((e) => [e.id, e]));
  const porNombre = new Map<string, (typeof delTelefono)[number]>();
  for (const e of delTelefono) {
    const clave = normalizar(e.nombre);
    // Entre dos con el mismo nombre, el que no está archivado
    const previo = porNombre.get(clave);
    if (previo === undefined || (previo.archivado === true && e.archivado !== true)) porNombre.set(clave, e);
  }

  const ejercicios: EjercicioVinculado[] = archivo.rutina.ejercicios.map((del) => {
    const porSuId = del.esBase ? porId.get(del.exerciseId) : undefined;
    const elegido = porSuId ?? porNombre.get(normalizar(del.nombre));
    if (elegido === undefined) {
      return { del, como: 'nuevo', exerciseId: null, nombre: del.nombre, nota: null };
    }
    const notas: string[] = [];
    if (del.esBase && porSuId === undefined) notas.push('se vinculó por su nombre, no por el catálogo');
    if (elegido.nombre !== del.nombre) notas.push(`en tu teléfono se llama "${elegido.nombre}"`);
    if (elegido.tipo !== del.tipo) notas.push('en tu teléfono es de otro tipo: revisa su objetivo');
    if (elegido.archivado === true) notas.push('lo tienes archivado');
    return {
      del,
      como: elegido.esBase ? 'base' : 'propio',
      exerciseId: elegido.id,
      nombre: elegido.nombre,
      nota: notas.length === 0 ? null : `${conMayuscula(notas.join('; '))}.`,
    };
  });

  const nuevos = ejercicios.filter((e) => e.como === 'nuevo').length;
  const advertencias: string[] = [];
  if (nuevos > 0) {
    advertencias.push(
      nuevos === 1
        ? 'Un ejercicio no lo tienes: se agrega a Mis ejercicios con su grupo, equipo y tipo. Su unidad de carga será la tuya.'
        : `${nuevos} ejercicios no los tienes: se agregan a Mis ejercicios con su grupo, equipo y tipo. Su unidad de carga será la tuya.`,
    );
  }
  if (ejercicios.some((e) => e.nota !== null)) {
    advertencias.push('Revisa los que tienen una nota: se vincularon con uno que ya tenías.');
  }
  return { nombre, ejercicios, nuevos, advertencias };
}

/** Un nombre que no choque con las rutinas que ya hay: "Lunes (2)". */
export function nombreLibreDeRutina(nombre: string, usados: readonly string[]): string {
  const ocupados = new Set(usados.map((n) => normalizar(n)));
  if (!ocupados.has(normalizar(nombre))) return nombre;
  for (let n = 2; n <= 99; n++) {
    const intento = `${nombre} (${n})`.slice(0, NOMBRE_MAXIMO);
    if (!ocupados.has(normalizar(intento))) return intento;
  }
  return nombre;
}
